import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Search, Filter, Info } from 'lucide-react';
type EventCategory = 'Admission' | 'Registration' | 'Academic' | 'Exam' | 'Holiday';
interface CalendarEvent {
  id: string;
  dateRange: string;
  title: string;
  category: EventCategory;
}
const events: CalendarEvent[] = [{
  id: '1',
  dateRange: 'May 05 - May 10',
  title: 'Online Admission for Summer 2026',
  category: 'Admission'
}, {
  id: '2',
  dateRange: 'May 12 - May 14',
  title: 'Advising for Summer 2026',
  category: 'Registration'
}, {
  id: '3',
  dateRange: 'May 15',
  title: 'Last date of Payment for Summer 2026',
  category: 'Registration'
}, {
  id: '4',
  dateRange: 'May 17',
  title: 'Classes Begin',
  category: 'Academic'
}, {
  id: '5',
  dateRange: 'May 17 - May 20',
  title: 'Online Add/Drop of Courses',
  category: 'Registration'
}, {
  id: '6',
  dateRange: 'May 26',
  title: 'Buddha Purnima (Holiday)',
  category: 'Holiday'
}, {
  id: '7',
  dateRange: 'Jun 15 - Jun 22',
  title: 'Midterm Examinations',
  category: 'Exam'
}, {
  id: '8',
  dateRange: 'Jun 28',
  title: 'Last day of withdrawal of courses (W)',
  category: 'Academic'
}, {
  id: '9',
  dateRange: 'Jul 01',
  title: 'Bank Holiday',
  category: 'Holiday'
}, {
  id: '10',
  dateRange: 'Jul 15',
  title: 'Ashura (Holiday)',
  category: 'Holiday'
}, {
  id: '11',
  dateRange: 'Aug 10',
  title: 'Last Day of Classes',
  category: 'Academic'
}, {
  id: '12',
  dateRange: 'Aug 12 - Aug 20',
  title: 'Final Examinations',
  category: 'Exam'
}, {
  id: '13',
  dateRange: 'Aug 24',
  title: 'Last date of grade submission',
  category: 'Academic'
}, {
  id: '14',
  dateRange: 'Aug 26',
  title: 'Publication of Results',
  category: 'Academic'
}];
const categoryColors: Record<EventCategory, string> = {
  Admission: 'border-orange-500 text-orange-700 bg-orange-50',
  Registration: 'border-teal text-teal bg-teal/10',
  Academic: 'border-blue-400 text-blue-700 bg-blue-50',
  Exam: 'border-red-500 text-red-700 bg-red-50',
  Holiday: 'border-green-500 text-green-700 bg-green-50'
};
export function CalendarPage() {
  const [activeSegment, setActiveSegment] = useState('Undergraduate');
  const [searchQuery, setSearchQuery] = useState('');
  const filteredEvents = useMemo(() => {
    if (!searchQuery) return events;
    const lowerQuery = searchQuery.toLowerCase();
    return events.filter(e => e.title.toLowerCase().includes(lowerQuery) || e.category.toLowerCase().includes(lowerQuery) || e.dateRange.toLowerCase().includes(lowerQuery));
  }, [searchQuery]);
  return <div className="bg-white min-h-screen pt-32 pb-24" data-id="element-592">
      <div className="max-w-7xl mx-auto px-6" data-id="element-593">
        {/* Header */}
        <div className="mb-12 text-center max-w-3xl mx-auto" data-id="element-594">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white border border-slate-200 mb-6 shadow-md" data-id="element-595">
            <Calendar className="w-8 h-8 text-gold" data-id="element-596" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 text-navy" data-id="element-597">
            Smart Academic Calendar
          </h1>
          <p className="text-lg text-slate-600" data-id="element-598">Summer Semester 2026</p>
        </div>

        {/* Controls Area */}
        <div className="bg-white border border-slate-200 shadow-md rounded-2xl p-4 md:p-6 mb-8 flex flex-col md:flex-row gap-6 items-center justify-between z-20 relative" data-id="element-599">
          {/* Segmented Selector */}
          <div className="flex bg-slate-100 p-1.5 rounded-xl border border-slate-200 w-full md:w-auto overflow-x-auto hide-scrollbar" data-id="element-600">
            {['Undergraduate', 'Graduate', 'Pharmacy & Law'].map(segment => <button key={segment} onClick={() => setActiveSegment(segment)} className={`px-6 py-2.5 rounded-lg text-sm font-bold whitespace-nowrap transition-all ${activeSegment === segment ? 'bg-gold text-white shadow-sm' : 'text-slate-600 hover:text-navy hover:bg-white'}`} data-id="element-601">
                {segment}
              </button>)}
          </div>

          {/* Search Input */}
          <div className="relative w-full md:w-96" data-id="element-602">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-603" />
            <input type="text" placeholder="Try: Add/Drop, Advising, Final Exams..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-white border border-slate-300 rounded-xl py-3 pl-12 pr-4 text-sm focus:outline-none focus:border-navy text-slate-800 transition-all placeholder:text-slate-400" data-id="element-604" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-navy text-xs font-bold" data-id="element-605">
                CLEAR
              </button>}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-606">
          {/* Calendar Matrix (Left) */}
          <div className="lg:w-3/4" data-id="element-607">
            <div className="bg-white border border-slate-200 shadow-md rounded-2xl overflow-hidden" data-id="element-608">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 p-4 md:p-6 border-b border-slate-200 bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider hidden md:grid" data-id="element-609">
                <div className="col-span-3" data-id="element-610">Date Range</div>
                <div className="col-span-7" data-id="element-611">Event Description</div>
                <div className="col-span-2 text-right" data-id="element-612">Category</div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-slate-200" data-id="element-613">
                <AnimatePresence data-id="element-614">
                  {filteredEvents.length > 0 ? filteredEvents.map((event, i) => <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.2,
                  delay: i * 0.03
                }} key={event.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 p-4 md:p-6 hover:bg-slate-50 transition-colors relative group" data-id="element-615">
                        {/* Color Code Border */}
                        <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${categoryColors[event.category].split(' ')[0]}`} data-id="element-616" />

                        <div className="md:col-span-3 font-medium text-slate-700 flex items-center pl-2 md:pl-0" data-id="element-617">
                          {event.dateRange}
                        </div>

                        <div className="md:col-span-7 text-lg md:text-base font-bold md:font-medium text-slate-900 pl-2 md:pl-0 group-hover:text-teal transition-colors" data-id="element-618">
                          {event.title}
                        </div>

                        <div className="md:col-span-2 flex items-center md:justify-end pl-2 md:pl-0 mt-2 md:mt-0" data-id="element-619">
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md border ${categoryColors[event.category]}`} data-id="element-620">
                            {event.category}
                          </span>
                        </div>
                      </motion.div>) : <div className="p-12 text-center text-slate-500" data-id="element-621">
                      <Filter className="w-8 h-8 mx-auto mb-3 opacity-50" data-id="element-622" />
                      <p data-id="element-623">No events found matching "{searchQuery}"</p>
                    </div>}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Legend Sidebar (Right) */}
          <div className="lg:w-1/4" data-id="element-624">
            <div className="bg-white border border-slate-200 shadow-md rounded-2xl p-6 sticky top-32" data-id="element-625">
              <h3 className="text-lg font-bold mb-6 flex items-center border-b border-slate-200 pb-4 text-navy" data-id="element-626">
                <Info className="w-5 h-5 mr-2 text-gold" data-id="element-627" />
                Legend
              </h3>

              <div className="space-y-4" data-id="element-628">
                {Object.entries(categoryColors).map(([category, classes]) => <div key={category} className="flex items-center space-x-3" data-id="element-629">
                    <div className={`w-3 h-3 rounded-full border ${classes.split(' ')[0]} ${classes.split(' ')[2]}`} data-id="element-630" />
                    <span className="text-sm font-medium text-slate-700" data-id="element-631">
                      {category}
                    </span>
                  </div>)}
              </div>

              <div className="mt-8 p-4 bg-slate-50 rounded-xl border border-slate-200" data-id="element-632">
                <p className="text-xs text-slate-500 leading-relaxed" data-id="element-633">
                  Dates are subject to change. Please check your EWU portal
                  regularly for personalized advising schedules.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}